import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { authFetch, refreshSession } from '@/lib/api-client';
import { getAccessToken, setAccessToken, setStoredRefreshToken } from '@/lib/token-store';
import { AuthUser, Session } from '@/types/auth';

const AUTH_USER_KEY = 'mms_auth_user';

function getStoredUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStoredUser(user: AuthUser | null) {
  if (typeof window === 'undefined') return;
  try {
    if (user) {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_USER_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Start with null / isLoading=true so server and client initial render match 100% (eliminates hydration error)
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // 1. Immediately hydrate cached user from localStorage on client mount
    const cachedUser = getStoredUser();
    if (cachedUser) {
      setUser(cachedUser);
      setIsLoading(false);
    }

    // 2. Validate/refresh session in background with backend
    refreshSession()
      .then((session) => {
        if (session?.user) {
          setUser(session.user);
          setStoredUser(session.user);
        } else if (!getAccessToken() && !cachedUser) {
          setUser(null);
          setStoredUser(null);
        }
      })
      .catch(() => {
        if (!getAccessToken() && !cachedUser) {
          setUser(null);
          setStoredUser(null);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  async function login(email: string, password: string) {
    const session = await authFetch<Session>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setAccessToken(session.accessToken);
    if (session.refreshToken) {
      setStoredRefreshToken(session.refreshToken);
    }
    setStoredUser(session.user);
    setUser(session.user);
  }

  async function logout() {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      setStoredRefreshToken(null);
      setStoredUser(null);
      setUser(null);
    }
  }

  function hasPermission(permission: string) {
    return user?.permissions.includes(permission) ?? false;
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
